/**
 * The Swaen — Kloosterzande, the Netherlands.
 *
 * A family maltster selling four named lines: plain Swaen base malts, the
 * drum-caramelised GoldSwaen range, the roasted BlackSwaen range, and the
 * PlatinumSwaen specialities. Every product page publishes an analysis table,
 * which is more than most maltsters of this size manage.
 *
 * ## Colour is derived, not copied
 *
 * Swaen print both EBC and °Lovibond, and the two do not agree with each other.
 * Their °L looks to be EBC halved rather than properly converted, which
 * overstates °L at the pale end — their Lager reads 1.7-2.1 °L against a
 * published 3-4 EBC, where the conversion this catalog uses gives 3.9. So the
 * stored °L is the midpoint of their published band and the EBC is derived from
 * it, which keeps the pair internally consistent and inside their own range.
 *
 * Two pages also disagree with themselves: the Rye malt's prose claims 4 EBC
 * where its table says 6-9, and the Naked Oat prose repeats the Pilsner malt's
 * figures verbatim. The analysis table wins in both.
 *
 * Diastatic power is published for five base malts only, as a WK minimum
 * converted at `Lintner = (WK + 16) / 3.5`. Everything else carries a
 * class-typical figure and says so.
 */

import type { Fermentable } from '../../schema/fermentable';

export const swaenFermentables: readonly Fermentable[] = [
  {
    id: 'swaen-pilsner',
    name: 'Swaen Pilsner Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 1.95,
    colorEbc: 4,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The Swaen’s everyday continental pilsner malt: pale, clean and well modified, with a Kolbach index of 35–45 and friability above 80 percent for easy conversion and good runoff. Enzyme reserve of at least 250 WK (about 76 °Lintner) leaves plenty of headroom for adjuncts, so it works as a full grist for lagers or as a neutral canvas under specialty malts in ales.',
    tags: ['base', 'pilsner', 'lager', 'dutch', 'low-colour'],
    url: 'https://theswaen.com/products/malt-for-beer/swaen-malts/swaen-pilsner-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'swaen-dutch-pilsner',
    name: 'Swaen Dutch Pilsner Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 1.95,
    colorEbc: 4,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Same pilsner specification as the standard Swaen Pilsner but malted from barley grown in the Netherlands under the maltster’s traceable local-sourcing program. Analysis matches its sibling — 81 percent dry extract, 4.5 percent moisture ceiling, at least 250 WK of enzyme — so it substitutes one for one when a recipe wants a single-origin Dutch grist.',
    tags: ['base', 'pilsner', 'lager', 'dutch', 'single-origin'],
    url: 'https://theswaen.com/products/malt-for-beer/swaen-malts/swaen-dutch-pilsner-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'swaen-lager',
    name: 'Swaen Lager Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 1.9,
    colorEbc: 3.9,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest of The Swaen’s base malts, kilned gently to keep color at the very bottom of the range and the flavor crisp rather than bready. Well modified, with beta-glucans held under 250 mg/l and viscosity under 1.6 mPa·s for clean lautering, and enzyme-rich enough at 250 WK minimum to carry rice, maize or other adjuncts in a light lager.',
    tags: ['base', 'lager', 'pale', 'dutch', 'low-colour'],
    url: 'https://theswaen.com/products/malt-for-beer/swaen-malts/swaen-lager-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'swaen-ale',
    name: 'Swaen Ale Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 3.5,
    colorEbc: 8.1,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A pale ale base kilned a touch warmer than the pilsner grades, giving a fuller, biscuity malt character while staying light enough for hop-forward beers. Kolbach index of 37–45 and friability above 80 percent point to thorough modification, and the 250 WK minimum enzyme reserve handles a grist built mostly from specialty malts.',
    tags: ['base', 'pale-ale', 'ale', 'dutch', 'biscuit'],
    url: 'https://theswaen.com/products/malt-for-beer/swaen-malts/swaen-ale-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'swaen-dutch-pale-ale',
    name: 'Swaen Dutch Pale Ale Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 3.5,
    colorEbc: 8.1,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale ale malt made entirely from Dutch-grown barley under the maltster’s traceable local-grain project, so field and batch can be followed through to the sack. The analysis mirrors the standard ale malt — 81 percent extract, 250 WK minimum, saccharification inside ten minutes — making it a drop-in swap when provenance matters to the recipe.',
    tags: ['base', 'pale-ale', 'dutch', 'single-origin', 'traceable'],
    url: 'https://theswaen.com/products/malt-for-beer/swaen-malts/dutch-pale-ale-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'swaen-vienna',
    name: 'Swaen Vienna Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'base',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 4.5,
    colorEbc: 10.8,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A warm-kilned Vienna malt with a soft toasty edge and enough color to give an amber lager its glow, usable as the whole grist. Extract, moisture and color are from the published analysis; diastatic power is a class-typical figure for Vienna malt because The Swaen does not print an enzyme value for this grade — it will convert itself but leaves little margin for adjuncts.',
    tags: ['base', 'vienna', 'amber', 'toasty', 'lager', 'dutch'],
    url: 'https://theswaen.com/products/malt-for-beer/swaen-malts/swaen-vienna-malt/',
    provenance: 'representative',
  },
  {
    id: 'swaen-munich-light',
    name: 'Swaen Munich Light Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.6,
    colorLovibond: 5.5,
    colorEbc: 13.4,
    diastaticPowerLintner: 45,
    suggestedMaxPercent: 75,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Lightly toasted bread and a rounded malt sweetness, with the body and head retention Munich is bought for. Color, extract and moisture are published; the enzyme figure here is class-typical, since The Swaen quotes only an apparent attenuation limit of 77 percent minimum rather than a diastatic power for this malt.',
    tags: ['munich', 'kilned', 'malty', 'bready', 'dutch'],
    url: 'https://theswaen.com/products/malt-for-beer/swaen-malts/swaen-munich-light-malt/',
    provenance: 'representative',
  },
  {
    id: 'swaen-munich-dark',
    name: 'Swaen Munich Dark Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 8,
    colorEbc: 20.1,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The deeper of the two Munich grades, kilned for a fuller malt aroma and a noticeably redder wort, at home in dunkel, bock and malt-forward ales. Extract, moisture and color come from the published analysis; the diastatic power is a class-typical value for dark Munich because no enzyme figure is printed — pair it with a high-enzyme base malt.',
    tags: ['munich', 'kilned', 'dark-malt', 'malty', 'bock', 'dutch'],
    url: 'https://theswaen.com/products/malt-for-beer/swaen-malts/swaen-munich-dark-malt/',
    provenance: 'representative',
  },
  {
    id: 'swaen-amber',
    name: 'Swaen Amber Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 19,
    colorEbc: 49.3,
    diastaticPowerLintner: 5,
    suggestedMaxPercent: 25,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A kilned color malt that carries cookie and nut notes alongside a golden-amber hue, adding malt backbone rather than caramel sweetness. Extract, moisture, color and wort pH are published; the enzyme value shown is class-typical for an amber malt, as the maltster prints none — treat it as contributing no meaningful conversion power.',
    tags: ['amber', 'kilned', 'colour-malt', 'nutty', 'biscuit', 'dutch'],
    url: 'https://theswaen.com/products/malt-for-beer/swaen-malts/swaen-amber-malt/',
    provenance: 'representative',
  },
  {
    id: 'swaen-melany',
    name: 'Swaen Melany Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 6.5,
    colorLovibond: 26.5,
    colorEbc: 69.3,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 25,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The Swaen’s melanoidin-style malt, stewed and kilned for baked-goods depth — golden biscuit, toasted nut and a rounded sweetness — while also firming up foam and body. Color, extract, moisture and pH are published; the diastatic power given is class-typical for melanoidin malt, since none is stated. A little goes a long way in bocks, dubbels and winter beers.',
    tags: ['melanoidin', 'kilned', 'malty', 'nutty', 'colour-malt', 'dutch'],
    url: 'https://theswaen.com/products/malt-for-beer/swaen-malts/swaen-melany-malt/',
    provenance: 'representative',
  },
  {
    id: 'swaen-wheat-classic',
    name: 'Swaen Wheat Classic Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 5.5,
    colorLovibond: 2,
    colorEbc: 4.1,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale malted wheat for hefeweizen, witbier and American wheat, and a reliable few percent in any beer that needs better head retention and a creamier mouthfeel. Protein runs up to 13.8 percent and viscosity to 1.9 mPa·s, so keep the lauter in mind. Extract, moisture and color are published; the enzyme figure is class-typical for wheat malt, which The Swaen does not quote.',
    tags: ['wheat', 'base', 'hefeweizen', 'witbier', 'head-retention', 'dutch'],
    url: 'https://theswaen.com/products/malt-for-beer/swaen-malts/swaen-wheat-classic-malt/',
    provenance: 'representative',
  },
  {
    id: 'swaen-wheat-dark',
    name: 'Swaen Wheat Dark Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 5.5,
    colorLovibond: 4.5,
    colorEbc: 10.8,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Wheat malt taken further up the kiln, adding toasted, faintly nutty depth and a deeper color while keeping wheat’s creamy body and foam. Suited to dunkelweizen and to darker barley beers that want a softer texture. Extract, moisture and color are published; the diastatic power shown is class-typical for a darker wheat malt, as none is printed.',
    tags: ['wheat', 'dark-wheat', 'dunkelweizen', 'toasty', 'kilned', 'dutch'],
    url: 'https://theswaen.com/products/malt-for-beer/swaen-malts/swaen-wheat-dark-malt/',
    provenance: 'representative',
  },
  {
    id: 'swaen-rye',
    name: 'Swaen Rye Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 5,
    colorLovibond: 3.35,
    colorEbc: 7.7,
    diastaticPowerLintner: 75,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned rye malt for a peppery, earthy, slightly fruity edge and a distinctly slick mouthfeel. Huskless and high in protein, so hold it near the suggested ceiling and expect a slow runoff — rice hulls help. Extract, moisture and color are published; the enzyme figure is class-typical for rye malt, which the maltster does not list.',
    tags: ['rye', 'spicy', 'huskless', 'specialty-grain', 'dutch'],
    url: 'https://theswaen.com/products/malt-for-beer/swaen-malts/swaen-rye-malt/',
    provenance: 'representative',
  },
  {
    id: 'swaen-spelt',
    name: 'Swaen Spelt Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 6,
    colorLovibond: 2.45,
    colorEbc: 5.3,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted spelt, an ancient wheat relative, bringing gentle spice and a hazelnut note plus a firmer structure to the finished beer. Protein reaches 17 percent, so it builds body and foam at modest additions. Extract, moisture, color and wort pH are published; the diastatic power given is class-typical for spelt malt, as no figure is stated.',
    tags: ['spelt', 'ancient-grain', 'nutty', 'specialty-grain', 'dutch'],
    url: 'https://theswaen.com/products/malt-for-beer/swaen-malts/swaen-spelt-malt/',
    provenance: 'representative',
  },
  {
    id: 'swaen-naked-oat',
    name: 'Swaen Naked Oat Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 76,
    moisturePercent: 5,
    colorLovibond: 2.25,
    colorEbc: 4.8,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted huskless oats for the silky, slightly viscous body and soft haze that hazy IPAs and oat stouts are built on. Protein of 11–17 percent and a published saccharification time of at least 120 minutes mark it out as slow-converting: mash it with a vigorous base malt and give it time. Extract, moisture and color are published; the enzyme value is class-typical for oat malt.',
    tags: ['oat', 'huskless', 'mouthfeel', 'haze', 'specialty-grain', 'dutch'],
    url: 'https://theswaen.com/products/malt-for-beer/swaen-malts/swaen-naked-oat-malt/',
    provenance: 'representative',
  },
  {
    id: 'blackswaen-barley-unmalted',
    name: 'BlackSwaen Barley Unmalted',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 73,
    moisturePercent: 4.5,
    colorLovibond: 422.5,
    colorEbc: 1122.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Raw barley taken straight to the drum roaster without ever being malted, so the husk and protein survive the heat intact. A few percent turns a beer near-black and lays down the dry, ashy bite and thick tan head that mark out a dry stout. The unconverted starch it carries needs a high-DP base malt alongside it.',
    tags: ['roasted', 'unmalted', 'barley', 'dutch', 'stout', 'head-retention'],
    url: 'https://theswaen.com/products/malt-for-beer/black-swaen-malts/black-swaen-barley-unmalted/',
    provenance: 'spec-sheet',
  },
  {
    id: 'blackswaen-biscuit',
    name: 'BlackSwaen Biscuit Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 31,
    colorEbc: 81.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The lightest malt in the roasted range: finished malt given a short, hot, dry pass through the drum so it browns without caramelizing. It reads as toast crust and warm nuts rather than roast, and a handful of percent gives a pale or amber ale the baked depth its base malt cannot reach on its own.',
    tags: ['roasted', 'biscuit', 'toasty', 'dutch', 'amber-ale'],
    url: 'https://theswaen.com/products/malt-for-beer/black-swaen-malts/black-swaen-biscuit-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'blackswaen-black-extra',
    name: 'BlackSwaen Black Extra Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 71,
    moisturePercent: 4.5,
    colorLovibond: 500,
    colorEbc: 1328.6,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest malt The Swaen roasts, taken a step past their standard black. Burnt coffee, bitter cocoa and a trace of smoke arrive with an almost opaque color, so it is dosed in ones and twos of a percent. Strongly acidic in the mash — count it in when you set your pH.',
    tags: ['roasted', 'black-malt', 'dutch', 'stout', 'schwarzbier'],
    url: 'https://theswaen.com/products/malt-for-beer/black-swaen-malts/black-swaen-black-extra-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'blackswaen-black',
    name: 'BlackSwaen Black Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 73,
    moisturePercent: 4.5,
    colorLovibond: 422.5,
    colorEbc: 1122.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The standard black malt of the range, pulled from the roaster a shade short of the Extra. It brings coloring power and a smoky, aromatic roast that reads louder on the nose than most dark malts, without the scorched edge of a deeper roast. The usual choice for putting the last of the color into a porter or a schwarzbier.',
    tags: ['roasted', 'black-malt', 'dutch', 'porter', 'colouring'],
    url: 'https://theswaen.com/products/malt-for-beer/black-swaen-malts/black-swaen-black-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'blackswaen-black-wheat',
    name: 'BlackSwaen Black Wheat Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 422.5,
    colorEbc: 1122.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Wheat malt roasted as far as their black barley malt goes, but with no husk to burn it comes out rounder and far less astringent. Use it when a beer needs the color of black malt and none of the rasp — dark wheat beers, but equally any style you want to darken without changing how it tastes.',
    tags: ['roasted', 'wheat', 'huskless', 'dutch', 'colouring'],
    url: 'https://theswaen.com/products/malt-for-beer/black-swaen-malts/black-swaen-black-wheat-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'blackswaen-chocolate-b',
    name: 'BlackSwaen Chocolate B Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 337.5,
    colorEbc: 896.4,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A step below the black malts, landing in deep brown rather than true black. Cocoa, roasted nuts and a hint of espresso come with a gentle roast bitterness that fills out a porter or brown ale without flattening it. The workhorse dark malt when you want richness and can still see through the glass at the edges.',
    tags: ['roasted', 'chocolate', 'cocoa', 'dutch', 'porter', 'brown-ale'],
    url: 'https://theswaen.com/products/malt-for-beer/black-swaen-malts/black-swaen-chocolate-b-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'blackswaen-chocolate-wheat',
    name: 'BlackSwaen Chocolate Wheat Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 337.5,
    colorEbc: 896.4,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The same roast depth as their Chocolate B, but on huskless wheat, which strips out most of the bitterness and leaves soft cocoa and mild toast. A ready substitute for dehusked roasted barley when a stout or dark ale is coming out harsher than you wanted.',
    tags: ['roasted', 'chocolate', 'wheat', 'huskless', 'dutch', 'stout'],
    url: 'https://theswaen.com/products/malt-for-beer/black-swaen-malts/black-swaen-chocolate-wheat-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'blackswaen-coffee',
    name: 'BlackSwaen Coffee Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 245,
    colorEbc: 650.4,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Roasted to the point where the aroma turns to fresh coffee, with nutty and creamy notes underneath. It sits between the chocolate and black malts, so it delivers plenty of coffee character before the color runs away — the usual pick for a stout or porter that should taste of coffee without anything being steeped in it.',
    tags: ['roasted', 'coffee', 'dutch', 'stout', 'porter'],
    url: 'https://theswaen.com/products/malt-for-beer/black-swaen-malts/black-swaen-coffee-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'blackswaen-honey-biscuit',
    name: 'BlackSwaen Honey Biscuit Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 32.5,
    colorEbc: 85.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A house speciality: biscuit character carried on a sweet, honeyed malt note with fresh bread behind it. Light enough that a fifth of the grist barely moves the color, so it is a way to add warmth and body to an ale or lager without darkening it.',
    tags: ['roasted', 'biscuit', 'honey', 'dutch', 'specialty'],
    url: 'https://theswaen.com/products/malt-for-beer/black-swaen-malts/black-swaen-honey-biscuit-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'platinumswaen-brown-porter',
    name: 'PlatinumSwaen Brown Porter Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 76,
    moisturePercent: 3.5,
    colorLovibond: 159.5,
    colorEbc: 423,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Made from green malt kilned in the drum at a held-down temperature rather than roasted, which keeps chocolate, coffee and caramel in and leaves the burnt notes out. That gentleness is why it can go to nearly a third of the grist — enough to build a whole historic brown porter rather than just tint one. Swaen say up to 30 per cent of the grist; this catalog caps roasted malts at 20, which at 160 °L is already a very dark beer.',
    tags: ['roasted', 'brown-malt', 'porter', 'dutch', 'kilned'],
    url: 'https://theswaen.com/products/malt-for-beer/platinum-swaen-malts/platinum-swaen-brown-porter-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'platinumswaen-chit',
    name: 'PlatinumSwaen Chit Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 50,
    moisturePercent: 6,
    colorLovibond: 1.45,
    colorEbc: 2.7,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Germination is cut short at the chit stage, so most of the grain is left unmodified and the large foam-positive proteins survive. Added at a few percent it thickens the head and the body without touching color or clarity. The published extract of 50 % is low by design; the maltster does not publish a diastatic power, so the figure here is a representative one and this malt should never be counted on for conversion.',
    tags: ['base', 'chit', 'foam', 'head-retention', 'dutch', 'under-modified'],
    url: 'https://theswaen.com/products/malt-for-beer/platinum-swaen-malts/platinum-swaen-chit-malt/',
    provenance: 'representative',
  },
  {
    id: 'platinumswaen-dextrin',
    name: 'PlatinumSwaen Dextrin Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'crystal-caramel',
    maltClass: 'non-buffering',
    extractFineDryPercent: 79,
    moisturePercent: 8,
    colorLovibond: 2.25,
    colorEbc: 4.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A carapils-style malt whose starch is converted to dextrins the yeast cannot ferment, so what it adds is body and foam rather than alcohol or color. Fermentability is capped at 50 %. It carries no enzymes and needs a well-modified base malt beside it; The Swaen push it as far as half the grist in alcohol-free beers, where thinness is the problem to solve.',
    tags: ['dextrin', 'carapils', 'body', 'foam', 'dutch', 'low-alcohol'],
    url: 'https://theswaen.com/products/malt-for-beer/platinum-swaen-malts/platinum-swaen-dextrin-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'platinumswaen-salty-caramel',
    name: 'PlatinumSwaen Salty Caramel Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 81.3,
    moisturePercent: 6.5,
    colorLovibond: 10,
    colorEbc: 25.4,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A light caramel malt carrying up to 1 % sea salt, so it does two jobs at once: the stewed sugars give the usual caramel sweetness, body and head retention, and the salt lifts and rounds the finish the way it does in salted caramel. That salt load is the reason it turns up in gose recipes; at 20 % it is a real salt addition, not a garnish.',
    tags: ['crystal', 'caramel', 'salt', 'gose', 'dutch', 'specialty'],
    url: 'https://theswaen.com/products/malt-for-beer/platinum-swaen-malts/platinum-swaen-salty-caramel-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'platinumswaen-sauer',
    name: 'PlatinumSwaen Sauer Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'base',
    maltClass: 'acidulated',
    extractFineDryPercent: 80,
    moisturePercent: 8,
    colorLovibond: 4.5,
    colorEbc: 10.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Barley malt soured with its own lactobacilli, used as a grain you can weigh instead of an acid you have to pipette. Roughly 1 % of the grist drops mash pH by about 0.1, which is how brewers on alkaline water pull a pale mash back into range without touching the ingredient list. The maltster publishes no extract or diastatic power, so both figures here are representative.',
    tags: ['acidulated', 'sauermalz', 'mash-ph', 'dutch', 'lactic'],
    url: 'https://theswaen.com/products/malt-for-beer/platinum-swaen-malts/platinum-swaen-sauer-malt/',
    provenance: 'representative',
  },
  {
    id: 'platinumswaen-smoke',
    name: 'PlatinumSwaen Smoke Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 77,
    moisturePercent: 5,
    colorLovibond: 3.5,
    colorEbc: 8.1,
    diastaticPowerLintner: 100,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale malt smoked over beech, well enough modified — friability above 75 %, Kolbach 36 to 44 — to carry a grist on its own, which is exactly what a rauchbier asks of it. The color stays low so the smoke, not the malt, is what you taste. No diastatic power is published, so the value here is representative for a malt of this modification.',
    tags: ['base', 'smoked', 'beechwood', 'rauchbier', 'dutch'],
    url: 'https://theswaen.com/products/malt-for-beer/platinum-swaen-malts/platinum-swaen-smoke-malt/',
    provenance: 'representative',
  },
  {
    id: 'platinumswaen-stroopwafel',
    name: 'PlatinumSwaen Stroopwafel Malt',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 79,
    moisturePercent: 6,
    colorLovibond: 26.5,
    colorEbc: 69.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Not a single malt but a blend of caramelized and roasted malts put together to land on one flavor: waffle biscuit, thick syrup caramel and a trace of cinnamon. In practice it behaves like a medium caramel malt — amber color, sweetness and body — with the cookie note on top. Suits dubbels, winter beers and dessert stouts.',
    tags: ['crystal', 'caramel', 'biscuit', 'blend', 'dutch', 'dubbel'],
    url: 'https://theswaen.com/products/malt-for-beer/platinum-swaen-malts/platinum-swaen-stroopwafel-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'swaen-flaked-oat',
    name: 'Flaked Oat',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 78,
    moisturePercent: 13,
    colorLovibond: 1,
    colorEbc: 1.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted oats rolled into flakes that go into the mash without milling. The beta-glucans and protein they carry are what give an oatmeal stout its slickness and a hazy IPA its standing turbidity — and what will gum up a lauter, so run rice hulls with them. The Swaen publish moisture and density but no color, extract or diastatic power; those three are representative here.',
    tags: ['adjunct', 'flaked', 'oats', 'mouthfeel', 'hazy', 'dutch'],
    url: 'https://theswaen.com/products/malt-for-beer/flakes/flaked-oat/',
    provenance: 'representative',
  },
  {
    id: 'swaen-flaked-wheat',
    name: 'Flaked Wheat',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 80,
    moisturePercent: 12.8,
    colorLovibond: 1.6,
    colorEbc: 3.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Raw wheat gelatinised between hot rollers, so it converts in an ordinary infusion mash and needs no milling. It tastes grainier and more rustic than malted wheat, and its protein is what holds the haze in a witbier or NEIPA and the foam on top. Color, extract and diastatic power are not published, so those figures are representative.',
    tags: ['adjunct', 'flaked', 'wheat', 'witbier', 'neipa', 'dutch'],
    url: 'https://theswaen.com/products/malt-for-beer/flakes/flaked-wheat/',
    provenance: 'representative',
  },
  {
    id: 'goldswaen-light',
    name: 'GoldSwaen Light',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 9,
    colorLovibond: 6,
    colorEbc: 14.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 50,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest of the GoldSwaen caramel drum malts, gently treated so it lifts sweetness, body and foam without moving the beer’s color much. Soft toffee and round malt sugars make it a quiet filler for pale lagers and session ales. Extract and moisture are published as minimum and maximum limits; diastatic power is zero, as with all drum-caramelized malts.',
    tags: ['crystal', 'caramel', 'dutch', 'pale', 'body'],
    url: 'https://theswaen.com/products/malt-for-beer/gold-swaen-malts/gold-swaen-light-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'goldswaen-hell',
    name: 'GoldSwaen Hell',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 79,
    moisturePercent: 8,
    colorLovibond: 10,
    colorEbc: 25.4,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 50,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A light caramel malt that warms a beer to golden without darkening it, carrying a delicate caramel sweetness. The Swaen point to better mouthfeel, head retention and colloidal stability, which suits it to helles, pilsner and pale ale grists at generous rates. Drum-caramelized, so it brings no enzymes of its own.',
    tags: ['crystal', 'caramel', 'dutch', 'golden', 'lager'],
    url: 'https://theswaen.com/products/malt-for-beer/gold-swaen-malts/gold-swaen-hell-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'goldswaen-belge',
    name: 'GoldSwaen Belge',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 9,
    colorLovibond: 14,
    colorEbc: 36.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Built for Belgian work: a honey-gold caramel malt with restrained sweetness that props up the malt backbone while leaving room for yeast esters and phenols. Blondes, abbey beers, dubbels and tripels are the obvious homes. Its high moisture ceiling reflects the gentle drum treatment used to keep the flavor delicate.',
    tags: ['crystal', 'caramel', 'dutch', 'belgian', 'abbey'],
    url: 'https://theswaen.com/products/malt-for-beer/gold-swaen-malts/gold-swaen-belge-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'goldswaen-red',
    name: 'GoldSwaen Red',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78,
    moisturePercent: 7,
    colorLovibond: 19,
    colorEbc: 49.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 35,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Saccharified and then caramelized in the drum roaster to give a warm red cast rather than simple amber. Toffee and caramel arrive in balance, adding sweetness and body without taking over, which is why it carries red ales, red lagers, altbier and amber IPAs. Usable up to about a third of the grist.',
    tags: ['crystal', 'caramel', 'dutch', 'red', 'toffee'],
    url: 'https://theswaen.com/products/malt-for-beer/gold-swaen-malts/gold-swaen-red-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'goldswaen-amber',
    name: 'GoldSwaen Amber',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78,
    moisturePercent: 6.5,
    colorLovibond: 26.5,
    colorEbc: 69.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A step darker and deeper than GoldSwaen Red, pushing toffee and rich caramel forward with a faint roasted edge underneath. Amber ales, altbier, bocks and darker specialities gain color, fuller body and steadier foam without extra bitterness. Drum-caramelized, so it contributes no diastatic power.',
    tags: ['crystal', 'caramel', 'dutch', 'amber', 'toffee'],
    url: 'https://theswaen.com/products/malt-for-beer/gold-swaen-malts/gold-swaen-amber-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'goldswaen-munich-light',
    name: 'GoldSwaen Munich Light',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 35,
    colorEbc: 91.9,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Despite the Munich name this is a caramelized drum malt, not a kilned base malt: pronounced caramel aroma, a reddish shine and an unusually high extract for its color. Good for rounding out lagers, amber ales and specialty brews where you want malt depth from a small addition.',
    tags: ['crystal', 'caramel', 'dutch', 'munich', 'reddish'],
    url: 'https://theswaen.com/products/malt-for-beer/gold-swaen-malts/gold-swaen-munich-light-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'goldswaen-classic',
    name: 'GoldSwaen Classic',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77,
    moisturePercent: 5.5,
    colorLovibond: 45,
    colorEbc: 118.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The mid-range workhorse of the caramel line, with a strong caramel and toffee aroma and a warm amber hue. It gives dark festival beers, bocks and strong ales a bold malty spine plus real body. Caramelized in the drum, so no enzymes: mash it alongside a base malt or steep it.',
    tags: ['crystal', 'caramel', 'dutch', 'toffee', 'bock'],
    url: 'https://theswaen.com/products/malt-for-beer/gold-swaen-malts/gold-swaen-classic-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'goldswaen-munich-dark',
    name: 'GoldSwaen Munich Dark',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77,
    moisturePercent: 5.5,
    colorLovibond: 55,
    colorEbc: 145.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A darker caramelized malt sitting just above Classic, with rich caramel at the front and a faint hint of roast behind it. The reddish warmth and rounded mouthfeel suit amber ales, bockbier and darker lagers. Like the rest of the GoldSwaen range it is drum-caramelized rather than kilned, so it carries no diastatic power.',
    tags: ['crystal', 'caramel', 'dutch', 'munich', 'dark'],
    url: 'https://theswaen.com/products/malt-for-beer/gold-swaen-malts/gold-swaen-munich-dark-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'goldswaen-wheat-light',
    name: 'GoldSwaen Wheat Light',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    colorLovibond: 34.5,
    colorEbc: 90.6,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Caramelized wheat, huskless and therefore free of harsh tannin bite, with almond, biscuit and fresh-bread notes. The published color spans a wide band, so treat the figure here as the mid-point. Its high extract reflects wheat’s starch-rich grain, and it adds body and soft mouthfeel to wheat beers.',
    tags: ['crystal', 'caramel', 'wheat', 'dutch', 'huskless'],
    url: 'https://theswaen.com/products/malt-for-beer/gold-swaen-malts/gold-swaen-wheat-light-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'goldswaen-wheat-dark',
    name: 'GoldSwaen Wheat Dark',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 66,
    colorEbc: 174.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darker caramelized wheat, reaching amber-to-brown depth while the huskless grain keeps the finish smooth instead of astringent. Dunkelweizen, schwarzbier, porter and stout use it for color and fullness at modest rates. High extract for its color, thanks to the wheat base.',
    tags: ['crystal', 'caramel', 'wheat', 'dutch', 'dunkelweizen'],
    url: 'https://theswaen.com/products/malt-for-beer/gold-swaen-malts/gold-swaen-wheat-dark-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'goldswaen-brown-light',
    name: 'GoldSwaen Brown Light',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 68,
    colorEbc: 179.7,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Bronze in the glass with an intense caramel-and-biscuit aroma, this is the entry point to the darker end of the GoldSwaen caramel malts. It thickens body and holds foam without straying into roast character, which makes it useful in bocks, export beers and low-alcohol brews that need more presence.',
    tags: ['crystal', 'caramel', 'dutch', 'brown', 'biscuit'],
    url: 'https://theswaen.com/products/malt-for-beer/gold-swaen-malts/gold-swaen-brown-light-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'goldswaen-brown',
    name: 'GoldSwaen Brown',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 82.5,
    colorEbc: 218.2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A step past Brown Light: deeper bronze, quieter caramel and a much more assertive biscuit note. Body, head formation and head retention all improve, so it earns its place in malt-forward ambers, bocks and export styles while stopping short of true roast bitterness.',
    tags: ['crystal', 'caramel', 'dutch', 'brown', 'biscuit'],
    url: 'https://theswaen.com/products/malt-for-beer/gold-swaen-malts/gold-swaen-brown-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'goldswaen-brown-supreme',
    name: 'GoldSwaen Brown Supreme',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 76,
    moisturePercent: 4.5,
    colorLovibond: 112.5,
    colorEbc: 298,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest of the three browns, sitting at the top of that range with the deepest bronze shine and the most concentrated biscuit aroma. Extract slips slightly against the lighter browns, as expected at this color. Keep it to a modest share of the grist for malt-forward and dark specialty beers.',
    tags: ['crystal', 'caramel', 'dutch', 'brown', 'dark'],
    url: 'https://theswaen.com/products/malt-for-beer/gold-swaen-malts/gold-swaen-brown-supreme-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'goldswaen-aroma',
    name: 'GoldSwaen Aroma',
    maker: 'The Swaen',
    origin: 'Netherlands',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 150,
    colorEbc: 397.7,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest caramel malt in the line, used more as a seasoning than a filler. Small additions throw a dark-reddish tint and bring refined nut and caramel complexity to porters, stouts and dark ales, while body and head retention improve alongside. Drum-caramelized, with no enzymic contribution.',
    tags: ['crystal', 'caramel', 'dutch', 'dark', 'nutty', 'stout'],
    url: 'https://theswaen.com/products/malt-for-beer/gold-swaen-malts/gold-swaen-aroma-malt/',
    provenance: 'spec-sheet',
  },
];
